import { useState } from 'react';
import { Dropdown, DropdownItem, DropdownMenu } from 'reactstrap';
import styled from 'styled-components';
import { FilterButton } from './FilterDropdown';
import {
  MULTI_YEAR,
  MULTI_YEAR_FILTERS,
  N_YEARS,
  ANNUAL_MULTI_YEAR,
  ANNUAL_MULTI_YEAR_FILTERS,
} from '../constants/years';
import { asQuantity } from '../utils/formatters';
import { useStartYear } from '../utils/hooks';

const StyledMenu = styled(DropdownMenu)`
  max-width: 300px;
  overflow-x: hidden;
  font-size: 13px;
  overflow-y: auto;
  max-height: 60vh;
`;

export const getOptionKey = (year, years) => `${year}:${years}`;

const cumulativeOptionGroup = {
  header: 'Cumulative',
  options: MULTI_YEAR_FILTERS.map((y) => ({
    year: MULTI_YEAR,
    years: y,
    key: getOptionKey(MULTI_YEAR, y),
    name: asQuantity(y, {
      unit: 'years',
      singular: 'year',
    }),
  })),
};

const annualMultiYearOptionGroup = {
  header: 'Annual - multi year',
  options: ANNUAL_MULTI_YEAR_FILTERS.map((y) => ({
    year: ANNUAL_MULTI_YEAR,
    years: y,
    key: getOptionKey(ANNUAL_MULTI_YEAR, y),
    name: asQuantity(y, {
      unit: 'years',
      singular: 'year',
    }),
  })),
};

const annualSingleYearOptionGroup = (farmStartYear) => ({
  header: 'Annual - single year',
  options: Array.from({ length: N_YEARS }).map((x, y) => ({
    year: y,
    years: 1,
    key: `${y}:${1}`,
    name: farmStartYear + y,
  })),
});

const GroupedYearSelect = ({
  setOption,
  selectedOptionKey,
  showCumulativeYearSelect,
  showAnnualMultiYearSelect,
  showAnnualSingleYearSelect,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((open) => !open);

  const farmStartYear = useStartYear();

  const optionGroups = [
    showCumulativeYearSelect && cumulativeOptionGroup,
    showAnnualMultiYearSelect && annualMultiYearOptionGroup,
    showAnnualSingleYearSelect && annualSingleYearOptionGroup(farmStartYear),
  ].filter(Boolean);

  const optionsList = optionGroups.flatMap(({ options }) => options);
  const allOptions = optionsList.reduce((acc, option) => {
    acc[option.key] = option;
    return acc;
  }, {});

  return (
    <Dropdown isOpen={dropdownOpen} toggle={toggle} className="mx-2">
      <FilterButton toggle size="sm" id="grouped-year-select" selected>
        {allOptions[selectedOptionKey]?.name}
      </FilterButton>
      <StyledMenu>
        {optionGroups.map(({ header, options }) => (
          <div key={header}>
            <DropdownItem header className="font-small">
              {header}
            </DropdownItem>
            {options.map((option) => {
              const optionString =
                typeof option === 'string' ? option : option.name;
              return (
                <DropdownItem
                  key={option.key}
                  onClick={() => setOption(option)}
                  className="text-truncate"
                  title={option.name}
                >
                  <span
                    className={
                      allOptions[selectedOptionKey]?.key === option.key
                        ? 'font-weight-bold'
                        : ''
                    }
                  >
                    {optionString}
                  </span>
                </DropdownItem>
              );
            })}
          </div>
        ))}
      </StyledMenu>
    </Dropdown>
  );
};

export default GroupedYearSelect;
